// A run's or a chat turn's thinking as one compact line ("Thinking · 14 updates · 38 s", pulsing
// while it streams) with View, which opens the whole of it in a scrollable dialog: thinking as
// readable paragraphs, interim messages, one line per tool call, and anything ocs-capture could
// not place as readable fields, never JSON (S137, S157).
import { Brain, Check, Copy, Info, Wrench, X } from "@phosphor-icons/react";
import { useState } from "react";
import { Button, Modal } from "rizzui";

import { useT } from "../i18n";
import { cn } from "../ui/cn";
import { clockTime, elapsed } from "../workloads/format";
import { type ThinkingGroup, type Thought, thoughtsText } from "./thinking";

function ThoughtView({ thought }: { thought: Thought }) {
  const t = useT().conversation.thinking;
  const time = thought.at !== undefined ? <span className="ms-2 font-mono text-[10.5px] text-gray-400">{clockTime(thought.at)}</span> : null;
  switch (thought.kind) {
    case "tool":
      return (
        <p className="m-0 flex min-w-0 items-center gap-2 text-[12.5px] text-gray-500" data-thought="tool">
          <Wrench weight="duotone" className="h-3.5 w-3.5 shrink-0" aria-label={t.tool} />
          <span className="shrink-0 font-mono text-gray-700">{thought.label}</span>
          <span className="min-w-0 [overflow-wrap:anywhere]">{thought.text}</span>
          {time}
        </p>
      );
    case "system":
      return (
        <div className="space-y-1" data-thought="system">
          <p className="m-0 flex items-center gap-1.5 text-[11.5px] font-medium text-gray-500">
            <Info weight="duotone" className="h-3.5 w-3.5" aria-hidden />
            {thought.label ?? t.system}
            {time}
          </p>
          <p className="m-0 whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-[12.5px] leading-relaxed text-gray-700 [overflow-wrap:anywhere] dark:bg-gray-100">{thought.text}</p>
        </div>
      );
    default: {
      const thinking = thought.kind === "thinking";
      return (
        <p
          className={cn("m-0 whitespace-pre-wrap break-words text-[13px] leading-relaxed", thinking ? "italic text-gray-600" : "text-gray-800", thought.redacted && "text-gray-400")}
          data-thought={thought.kind}
        >
          {thought.redacted ? t.redacted : thought.text}
          {thought.partial ? <span className="ms-1.5 animate-pulse text-[11px] not-italic text-gray-400">· {t.streaming}</span> : null}
          {time}
        </p>
      );
    }
  }
}

export function ThinkingRow({ group, className, testId = "thinking" }: { group: ThinkingGroup; className?: string; testId?: string }) {
  const { conversation, common } = useT();
  const t = conversation.thinking;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const parts = group.updates > 0 ? [t.updates(group.updates)] : [];
  if (group.startedAt !== undefined && group.endedAt !== undefined && group.endedAt > group.startedAt) parts.push(elapsed(group.startedAt, group.endedAt));
  const copy = async () => {
    await navigator.clipboard?.writeText(thoughtsText(group, t.redacted));
    setCopied(true);
  };
  return (
    <div className={cn("flex min-w-0 items-center gap-2 rounded-lg border border-muted bg-gray-50 px-3 py-2 text-[12.5px] text-gray-600 dark:bg-gray-100", className)} data-testid={testId} data-live={group.live}>
      <Brain weight="duotone" className={cn("h-4 w-4 shrink-0 text-secondary", group.live && "animate-pulse")} aria-hidden />
      <span className={cn("shrink-0 font-medium text-gray-700", group.live && "animate-pulse")}>{group.live ? t.live : t.title}</span>
      {parts.length > 0 ? <span className="min-w-0 truncate text-gray-500">· {parts.join(" · ")}</span> : null}
      {group.thoughts.length > 0 ? (
        <button
          type="button"
          className="ms-auto shrink-0 rounded px-1 font-medium text-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          aria-label={t.viewLabel}
          onClick={() => {
            setCopied(false);
            setOpen(true);
          }}
        >
          {t.view}
        </button>
      ) : null}
      <Modal isOpen={open} onClose={() => setOpen(false)} size="lg" containerClassName="dark:bg-gray-100 max-sm:!w-full">
        <div className="flex max-h-[85vh] flex-col p-5 sm:p-6" role="group" aria-label={t.title}>
          <div className="mb-3 flex items-start gap-3">
            <Brain weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-secondary" aria-hidden />
            <div className="min-w-0 flex-1">
              <h2 className="m-0 text-base">
                {t.title} {parts.length > 0 ? <span className="text-[13px] font-normal text-gray-500">· {parts.join(" · ")}</span> : null}
              </h2>
              <p className="mb-0 mt-0.5 text-[13px] text-gray-500">{t.dialogHint}</p>
            </div>
            <button type="button" className="shrink-0 rounded p-1 text-gray-500 hover:text-gray-900" aria-label={common.actions.close} onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
            </button>
          </div>
          <div data-testid="thinking-full" className="custom-scrollbar max-h-[70vh] min-h-0 flex-1 space-y-3 overflow-y-auto rounded-lg border border-muted p-3">
            {group.thoughts.map((thought, i) => (
              <ThoughtView key={i} thought={thought} />
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" variant="outline" className="gap-1" onClick={() => void copy()}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? common.output.copied : common.actions.copy}
            </Button>
            <Button size="sm" onClick={() => setOpen(false)}>
              {common.actions.close}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
